import React, { useState, useEffect, useRef } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Swal from 'sweetalert2';
import PCNChecksAccordion from './PCNChecksAccordion';
import ReferenceContinuityNotice, { useReferenceContinuity } from './ReferenceContinuityNotice';
import PCNService from '../../Services/pcn.service';
import TaxAuthorityService from '../../Services/taxAuthority.service';
import EnumsService from '../../Services/enums.service';
import Constants from '../../assests/Constants/constants';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import { toast } from 'react-toastify';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { translateShaamMessage } from '../../Utils/shaamMessages';
import XtramModal, { XtramButton } from '../Widgets/XtramModal/XtramModal';
import styles from './PCNXtram.module.scss';

const cx = (...classes) => classes.filter(Boolean).join(' ');

const VAT_REPORT_ACTIONS = [
    { value: 1, labelKey: 'VatReportActionReport' },
    { value: 2, labelKey: 'VatReportActionCheck' },
    { value: 3, labelKey: 'VatReportActionCheckAndReportIfCorrect' },
];

const PCNChecksModal = ({
    open,
    onClose,
    direction,
    t,
    pcnHighConfirmationDocs,
    pcnDocumentsNeedingAllocation,
    pcnDocumentsWithoutReference,
    pcnDocumentsOutOfRange,
    pcnReportDetails,
    onDocumentClick,
    setShowPCNModal,
    handlePCNDownload,
    startDate,
    endDate,
    caseId: propCaseId,
    token: propToken,
    includeStornoChecks = null,
    viewDocumentDetails,
    sortCodeIdOptions = [],
    accountCardOptions = [],
}) => {
    const { menuProps } = useHamburgerMenu();
    const [downloading, setDownloading] = useState(false);
    const [sendVatReportLoading, setSendVatReportLoading] = useState(false);
    const [vatReportAction, setVatReportAction] = useState(2);   // 1 = report, 2 = check, 3 = check and report only if 100% correct
    const [resumeAfterAuth, setResumeAfterAuth] = useState(false);
    const [vatReportResult, setVatReportResult] = useState(null);
    const pendingActionRef = useRef(null);
    const [localSortCodeIdOptions, setLocalSortCodeIdOptions] = useState([]);
    const [localAccountCardOptions, setLocalAccountCardOptions] = useState([]);

    // Get caseId, accountantId and token from props or context (menuProps)
    const caseId = propCaseId || menuProps?.client?.caseId;
    const accountantId = menuProps?.client?.accountantId;
    const token = propToken || menuProps?.token;

    const referenceResult = useReferenceContinuity({ open, caseId, token, start: startDate, end: endDate });

    useEffect(() => {
        if (open) return;
        setVatReportResult(null);
        setLocalSortCodeIdOptions([]);
        setLocalAccountCardOptions([]);
    }, [open]);

    // Fallback: fetch sort codes and account cards when modal opens if parent did not pass them (so Debit/Credit show full labels)
    useEffect(() => {
        if (!open || !token) return;
        const needSortCodes = !sortCodeIdOptions?.length && accountantId;
        const needAccountCards = !accountCardOptions?.length && caseId;
        if (!needSortCodes && !needAccountCards) return;

        let cancelled = false;
        const fetchOptions = async () => {
            try {
                if (needSortCodes) {
                    const res = await EnumsService.GetSortCodesByAccountantId(accountantId, token);
                    if (res?.ok && !cancelled) {
                        const data = await res.json();
                        setLocalSortCodeIdOptions(Array.isArray(data) ? data : []);
                    }
                }
                if (needAccountCards) {
                    const res = await EnumsService.GetAccountCardsByCaseId(caseId, token);
                    if (res?.ok && !cancelled) {
                        const data = await res.json();
                        const list = Array.isArray(data) ? data : [];
                        setLocalAccountCardOptions(list.filter(opt => opt.cardId != null || opt.CardId != null));
                    }
                }
            } catch (e) {
                if (!cancelled) console.error('PCNChecksModal fallback fetch options:', e);
            }
        };
        fetchOptions();
        return () => { cancelled = true; };
    }, [open, token, caseId, accountantId, sortCodeIdOptions?.length, accountCardOptions?.length]);

    // ── Token auth flow (same pattern as VatModal) ──────────────────────────────
    const startAuthCompletionCheck = (authWindow) => {
        let attempts = 0;
        const maxAttempts = 25;

        const checkAuthCompletion = async () => {
            try {
                const result = await TaxAuthorityService.checkAuthCompletionAccountant(
                    accountantId,
                    Constants.TaxAuthorityScopes['VatReport'],
                    token
                );
                if (result.completed) {
                    authWindow.close();
                    setResumeAfterAuth(true);
                } else {
                    attempts += 1;
                    if (attempts < maxAttempts) {
                        setTimeout(checkAuthCompletion, 3000);
                    } else {
                        setSendVatReportLoading(false);
                        toast.error(t('TokenRetrievalFailed') || 'Token retrieval failed after maximum attempts.');
                        authWindow.close();
                    }
                }
            } catch (err) {
                console.error('Error checking auth completion:', err);
                toast.error(t('ErrorCheckingAuthCompletion') || t('Error'));
                authWindow.close();
                setSendVatReportLoading(false);
            }
        };

        checkAuthCompletion();
    };

    // Resume sending after auth window completes
    useEffect(() => {
        if (resumeAfterAuth && pendingActionRef.current != null) {
            setResumeAfterAuth(false);
            sendVatDetailedReportToApi(pendingActionRef.current);
        }
    }, [resumeAfterAuth]);

    const sendVatDetailedReportToApi = async (action) => {
        setSendVatReportLoading(true);
        try {
            const dateVal = startDate?.toDate?.() || startDate;
            const endDateVal = endDate?.toDate?.() || endDate;
            const result = await PCNService.SendVatDetailedReporting({
                caseId,
                action,
                date: dateVal,
                endDate: endDateVal,
                includeStornoChecks,
            });
            const ri = result?.report_information ?? result?.reportInformation;
            const summaryFromResult = result?.report_summary ?? result?.reportSummary;
            setVatReportResult({
                ...result,
                _action: action,
                report_information: ri,
                report_summary: summaryFromResult,
            });
            const hasError = ri?.error_code != null && ri?.error_code !== 0;
            if (hasError) {
                const errMsg = ri.error_message || ri.errorMessage || result?.message || t('SendVatDetailedReportIncorrect');
                const errCode = ri.error_code ?? 'N/A';
                return;
            }
            // Report the authority's own wording rather than assuming "no incorrect invoices":
            // a report can come back with error_code 0 and still contain incorrect invoices.
            const successMsg = translateShaamMessage(result?.message, t) || t('SendVatDetailedReportSuccess');
            const showSummarySwal = summaryFromResult && (action === 1 || action === 3);
            if (showSummarySwal) {
                const summary = summaryFromResult;
                const exempt = summary.exempt_transactions ?? summary.exemptTransactions ?? 0;
                const taxable = summary.taxable_transactions ?? summary.taxableTransactions ?? 0;
                const transVat = summary.transaction_vat ?? summary.transactionVat ?? 0;
                const otherInput = summary.other_input_vat ?? summary.otherInputVat ?? 0;
                const equipInput = summary.equipment_input_vat ?? summary.equipmentInputVat ?? 0;
                const amountPayRefund = summary.amount_to_pay_or_refund ?? summary.amountToPayOrRefund ?? 0;
                const dir = direction === 'rtl' ? 'rtl' : 'ltr';
                const textAlign = direction === 'rtl' ? 'right' : 'left';
                const valueAlign = direction === 'rtl' ? 'left' : 'right';
                const detailsHtml = `<div dir="${dir}" style="margin-top: 12px; font-size: 13px;">
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr style="background: #f0f2f5;"><td style="padding: 6px 8px; text-align: ${textAlign}">${t('TotalTaxableTransactions')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(taxable))} ש"ח</td></tr>
                    <tr><td style="padding: 6px 8px; text-align: ${textAlign}">${t('VATOnTaxableTransactions')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(transVat))} ש"ח</td></tr>
                    <tr style="background: #f0f2f5"><td style="padding: 6px 8px; text-align: ${textAlign}">${t('ExemptZeroTransactions')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(exempt))} ש"ח</td></tr>
                    <tr><td style="padding: 6px 8px; text-align: ${textAlign}">${t('EquipmentInputs')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(equipInput))} ש"ח</td></tr>
                    <tr style="background: #f0f2f5"><td style="padding: 6px 8px; text-align: ${textAlign}">${t('OtherInputs')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(otherInput))} ש"ח</td></tr>
                    <tr><td style="padding: 6px 8px; text-align: ${textAlign}">${amountPayRefund > 0 ? t('VATToPay') : t('AmountForRefundTransmitted')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600; ${amountPayRefund < 0 ? 'color: #d32f2f;' : ''}">${amountPayRefund < 0 ? `(${beautifyNumber(Math.abs(Math.round(amountPayRefund)))})` : beautifyNumber(Math.round(amountPayRefund))} ש"ח</td></tr>
                  </table>
                </div>`;
                const successIconHtml = '<div style="width:80px;height:80px;margin:0 auto 16px;border-radius:50%;background:#a5dc86;display:flex;align-items:center;justify-content:center"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg></div>';
                Swal.fire({
                    icon: 'success',
                    iconHtml: successIconHtml,
                    title: successMsg,
                    html: detailsHtml,
                    customClass: { container: 'swal-high-zindex', popup: direction === 'rtl' ? 'swal2-rtl' : '' },
                });
            } else {
                toast.success(successMsg);
            }
        } catch (err) {
            const errorPayload = err?.error ?? err?.details?.error ?? {};
            const code = errorPayload.code ?? errorPayload.statusCode ?? errorPayload.Code ?? errorPayload.StatusCode;
            if (code === 401 || code === '401') {
                toast.error(t('SendVatDetailedReportUnauthorized'), { autoClose: false });
                return;
            }
            // Transport failures carry no tax-authority error number, so the generic
            // "number + support message" toast would show the raw server text. Map them to their
            // own translated message instead.
            const transportErrorKeys = {
                504: 'SendVatDetailedReportTimeout',
                502: 'SendVatDetailedReportUnreachable',
                429: 'SendVatDetailedReportRateLimited',
            };
            const transportErrorKey = transportErrorKeys[Number(code)];
            if (transportErrorKey) {
                toast.error(t(transportErrorKey), { autoClose: false });
                return;
            }
            const errorNumber = errorPayload.errorNumber ?? errorPayload.ErrorNumber ?? 'N/A';
            const supportMessage = errorPayload.supportMessage ?? errorPayload.SupportMessage
                ?? errorPayload.errorDescription ?? errorPayload.ErrorDescription
                ?? (err?.message) ?? t('SendVatDetailedReportFailed');

            const errorMessage = err?.details?.message || err?.message || t('SendVatDetailedReportFailed');
            const detailedErrorMessage = `${errorMessage}\n\n❌ Error Number: ${errorNumber}\n💬 Support Message: ${supportMessage}`;
            toast.error(detailedErrorMessage, { autoClose: false });
        } finally {
            setSendVatReportLoading(false);
        }
    };

    const handleSendVatDetailedReport = async () => {
        if (!caseId) {
            toast.error(t('SendVatDetailedReportFailed') || t('Error'));
            return;
        }
        if (!accountantId) {
            toast.error(t('AccountantIdRequiredForVatReport') || t('SendVatDetailedReportFailed') || t('Error'));
            return;
        }

        const isReportAction = vatReportAction === 1 || vatReportAction === 3;
        if (isReportAction) {
            const { isConfirmed } = await Swal.fire({
                icon: 'warning',
                title: t('VatReportSendConfirmTitle') || 'Confirm send to tax authority',
                text: t('VatReportSendConfirmDescription') || 'This report will be sent to the tax authority and cannot be changed after submission. Do you want to continue?',
                showCancelButton: true,
                confirmButtonText: t('Confirm') || 'Confirm',
                cancelButtonText: t('Cancel') || 'Cancel',
                customClass: { container: 'swal-high-zindex' },
            });
            if (!isConfirmed) return;
        }

        pendingActionRef.current = vatReportAction;
        setSendVatReportLoading(true);

        try {
            const tokenData = await TaxAuthorityService.requestTokenAccountant(
                accountantId,
                Constants.TaxAuthorityScopes['VatReport'],
                token
            );

            if (tokenData?.authUrl) {
                const authWindow = window.open(tokenData.authUrl, '_blank');
                startAuthCompletionCheck(authWindow);
                return;
            }

            await sendVatDetailedReportToApi(vatReportAction);
        } catch (err) {
            console.error('Error in token request flow:', err);
            toast.error(t('ErrorFetchingToken') || t('SendVatDetailedReportFailed'));
            setSendVatReportLoading(false);
        }
    };

    const hasIssueDocs = [pcnHighConfirmationDocs, pcnDocumentsNeedingAllocation, pcnDocumentsWithoutReference, pcnDocumentsOutOfRange]
        .some((list) => list?.length > 0);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await handlePCNDownload(startDate, endDate);
        } finally {
            setDownloading(false);
        }
        onClose();
    };

    const busy = sendVatReportLoading || downloading;
    const actionLabel = t(VAT_REPORT_ACTIONS.find((a) => a.value === vatReportAction)?.labelKey);

    const footer = (
        <>
            <XtramButton variant="secondary" onClick={onClose} disabled={busy}>
                {t('Close')}
            </XtramButton>
            <div className={styles.footerSpacer} />
            {hasIssueDocs && (
                <XtramButton variant="secondary" onClick={handleDownload} busy={downloading} disabled={busy}>
                    <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                    {t('DownloadPCNReport')}
                </XtramButton>
            )}
            <XtramButton
                onClick={handleSendVatDetailedReport}
                busy={sendVatReportLoading}
                disabled={busy || !caseId || !accountantId || !startDate || !endDate}
            >
                <SendOutlinedIcon sx={{ fontSize: 18 }} />
                {actionLabel}
            </XtramButton>
        </>
    );

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('PCNDocumentsNeedingConfirmation')}
            direction={direction}
            expanded
            footer={footer}
        >
            <div className={styles.stack}>
                <ReferenceContinuityNotice
                    t={t}
                    result={referenceResult}
                    onViewIncomes={viewDocumentDetails ? () => { onClose(); viewDocumentDetails('Incomes'); } : undefined}
                />

                <PCNChecksAccordion
                    pcnHighConfirmationDocs={pcnHighConfirmationDocs}
                    pcnDocumentsNeedingAllocation={pcnDocumentsNeedingAllocation}
                    pcnDocumentsWithoutReference={pcnDocumentsWithoutReference}
                    pcnDocumentsOutOfRange={pcnDocumentsOutOfRange}
                    pcnReportDetails={pcnReportDetails}
                    onDocumentClick={onDocumentClick}
                    vatReportResult={vatReportResult}
                    t={t}
                    direction={direction}
                    pcnCheckStartDate={startDate}
                    pcnCheckEndDate={endDate}
                    sortCodeIdOptions={sortCodeIdOptions?.length ? sortCodeIdOptions : localSortCodeIdOptions}
                    accountCardOptions={accountCardOptions?.length ? accountCardOptions : localAccountCardOptions}
                />

                <div className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <span>{t('VatReportActionLabel')}</span>
                    </div>
                    <div className={styles.panelBody}>
                        <div className={styles.optionsRow} role="radiogroup">
                            {VAT_REPORT_ACTIONS.map((option) => {
                                const active = vatReportAction === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        disabled={sendVatReportLoading}
                                        className={cx(styles.option, active && styles.optionActive)}
                                        onClick={() => setVatReportAction(option.value)}
                                    >
                                        <span className={styles.optionDot} />
                                        {t(option.labelKey)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </XtramModal>
    );
};

export default PCNChecksModal;
